import { Injectable } from '@angular/core';
import { Observable, forkJoin, fromEvent, of, timer } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { ErrorService } from '../../services/error/error.service';

@Injectable({
  providedIn: 'root',
})
export class PrintService {
  constructor(private errorService: ErrorService) {}

  public printApplication(
    applicationName,
    applicationId: string,
    appendResultPage: boolean,
    includeRegulatoryPage: boolean = false
  ): Observable<any> {
    const printPages$ = [
      this.getPrintDataFromPage(`/${applicationName}/technical-data/${applicationId}`),
      this.getPrintDataFromPage(`/${applicationName}/personal-data/${applicationId}`),
      this.getPrintDataFromPage(`/${applicationName}/documents/${applicationId}`, !appendResultPage),
    ];

    if (appendResultPage) {
      printPages$.push(this.getPrintDataFromPage(`/${applicationName}/result/${applicationId}`, true));
    }
    if (includeRegulatoryPage) {
      printPages$.splice(1, 0, this.getPrintDataFromPage(`/${applicationName}/regulatory-data/${applicationId}`));
    }

    return forkJoin(printPages$).pipe(
      switchMap(pages => {
        const iframe = document.createElement('iframe');
        iframe.src = `src='about:blank'`;
        document.body.appendChild(iframe);

        return fromEvent(iframe, 'load').pipe(
          map(() => ({
            iframe,
            pages,
          }))
        );
      }),
      switchMap(({ iframe, pages }) => {
        iframe.contentWindow.document.body.innerHTML = '<app-root></app-root>';
        for (let index = 0; index < pages.length; index++) {
          const p = pages[index] as { head; body; iframe };
          if (!p.head && !p.body) {
            this.errorService.showError('Something went wrong, please retry or contact administrator!', 10000);
            document.body.removeChild(iframe);
            return of(null);
          } else {
            iframe.contentWindow.document.head.append(p.head);
            iframe.contentWindow.document.body.append(p.body);
            document.body.removeChild(p.iframe);
          }
        }
        return this.buildDelayedResultObservable(1000, iframe);
      }),
      switchMap(iframe => {
        if (!iframe) {
          return of(null);
        }
        iframe.contentWindow.document.body.querySelector('app-root')?.remove();
        iframe.contentWindow.print();
        document.body.removeChild(iframe);
        return of(null);
      })
    );
  }

  private getPrintDataFromPage(url: string, keepHistoryLogs = false): Observable<{ head: HTMLHeadElement; body: HTMLElement }> {
    const iframe = document.createElement('iframe');
    iframe.src = url;
    document.body.appendChild(iframe);
    iframe.setAttribute('hidden', 'true');

    return fromEvent(iframe, 'load').pipe(
      take(1),
      switchMap(() => {
        iframe.contentWindow.onafterprint = function () {
          document.body.removeChild(iframe);
        };

        return this.buildDelayedResultObservable(3000);
      }),
      switchMap(() => {
        const printFormElement = iframe.contentWindow.document.getElementById('print-form');
        if (!printFormElement) {
          return this.buildEmptyResultObservable(iframe);
        }

        this.enableSectionsForPrint(printFormElement);
        this.processHistoryLogsForPrint(printFormElement, keepHistoryLogs);

        const head = iframe.contentDocument.head;
        return this.buildDelayedResultObservable(500, { head, body: printFormElement, iframe: iframe });
      })
    );
  }

  // remove disabled-section class from all dom elements
  private enableSectionsForPrint(el: HTMLElement) {
    el.querySelectorAll('.disable-section').forEach(div => {
      div.classList.remove('disable-section');
    });
  }

  // remove history logs element or expand all rows there
  private processHistoryLogsForPrint(el: HTMLElement, keepHistoryLogs: boolean) {
    if (!keepHistoryLogs) {
      el.querySelector('app-order-history')?.parentElement?.removeChild(el.querySelector('app-order-history'));
      return;
    }

    // change logs to 100 rows
    var paginator = el.querySelector('app-order-history').querySelector('p-paginator');
    (paginator.querySelector('p-dropdown').querySelector('.ui-dropdown-trigger') as HTMLButtonElement).click();
    var dropdownItems = paginator.querySelectorAll('p-dropdownitem');
    dropdownItems[dropdownItems.length - 1].querySelector('li').click();

    // expand all rows
    el.querySelector('app-order-history')
      .querySelectorAll('.ui-accordion-header')
      .forEach(div => {
        (div.childNodes[0] as HTMLAnchorElement).click();
      });
  }

  private buildDelayedResultObservable<TData>(delay: number, data?: TData): Observable<TData> {
    return timer(delay).pipe(
      take(1),
      map(() => data)
    );
  }

  private buildEmptyResultObservable(iframe) {
    return of({ head: null, body: null, iframe: iframe });
  }
}
